import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkle, Compass, Lightning, MoonStars } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c31336c6-06bb-4c14-ba35-408cc1c327ae/images/0335c6fa31869e1133c03f5dcbc4efd868ff0c815ba2026d7d568d125a41e407.png";
const JOURNAL_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c31336c6-06bb-4c14-ba35-408cc1c327ae/images/a8219be1170f22a9aab9ecdff9008654b18c7dea15934f182c9f4533d298f633.png";
const LIGHT_IMG =
  "https://images.unsplash.com/photo-1764867249027-06a1db1c6613?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb3JuaW5nJTIwc3VubGlnaHQlMjBzaGFkb3dzJTIwd2FsbCUyMGNhbG18ZW58MHx8fHwxNzc4NzgyNzk2fDA&ixlib=rb-4.1.0&q=85";
const INSIGHTS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c31336c6-06bb-4c14-ba35-408cc1c327ae/images/cbd952d02c0307cede37942fbcbf33c1fd0b6707bd69350ccffc4d943105121d.png";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [year, setYear] = useState(2026);
  useEffect(() => setYear(new Date().getFullYear()), []);

  const fade = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] },
  };

  return (
    <div className="bg-canvas text-ink" data-testid="landing-page">
      {/* Nav */}
      <nav className="px-5 sm:px-8 md:px-16 py-5 sm:py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-7 h-7 rounded-full bg-clay/90 grid place-items-center">
            <Sparkle weight="fill" className="text-canvas" size={14} />
          </div>
          <span className="font-serif-display text-xl sm:text-2xl">Clarity Mirror</span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-6">
          <a href="#how" className="hidden md:inline text-sm text-ink-soft hover:text-ink transition-colors">
            How it works
          </a>
          <a href="#features" className="hidden md:inline text-sm text-ink-soft hover:text-ink transition-colors">
            Features
          </a>
          {user ? (
            <button
              data-testid="nav-open-app"
              onClick={() => nav("/app")}
              className="rounded-full bg-ink text-canvas px-4 sm:px-5 py-2 text-xs sm:text-sm hover:bg-ink/90 transition-colors"
            >
              Open journal
            </button>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-signin"
              className="rounded-full bg-ink text-canvas px-4 sm:px-5 py-2 text-xs sm:text-sm hover:bg-ink/90 transition-colors"
            >
              Begin
            </Link>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="px-5 sm:px-8 md:px-16 pt-10 pb-16 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-10 md:gap-16 items-end">
        <motion.div {...fade} className="md:col-span-7">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute mb-8">
            ✶ A quiet practice for clarity
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            See your <em className="italic text-clay">emotions</em>,<br />
            your <em className="italic text-clay">patterns</em>,<br />
            your <em className="italic text-clay">choices</em>.
          </h1>
          <p className="mt-6 sm:mt-8 text-base sm:text-lg text-ink-soft max-w-xl leading-relaxed">
            A journal that listens carefully and mirrors back what it notices,
            so the way forward stops feeling like a guess.
          </p>
          <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4">
            <Link
              to={user ? "/app" : "/auth"}
              data-testid="hero-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-clay text-canvas px-7 py-3.5 text-sm font-medium hover:bg-clay-dark transition-all active:scale-95"
            >
              Start writing
              <ArrowRight weight="bold" size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="#how" className="text-sm text-ink-soft hover:text-ink transition-colors underline underline-offset-4 decoration-line-strong">
              See how it works
            </a>
          </div>
        </motion.div>

        <motion.div
          {...fade}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
          className="md:col-span-5"
        >
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-canvas-warm">
            <img src={HERO_IMG} alt="Soft clay shapes in warm light" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-line/60 rounded-[2rem]" />
          </div>
        </motion.div>
      </section>

      {/* Bento: how it works */}
      <section id="how" className="px-5 sm:px-8 md:px-16 pb-16 sm:pb-24">
        <div className="mb-10 sm:mb-12 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute mb-4">The practice</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-tight">
            Four small movements,<br />one clearer mind.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <BentoCard
            className="md:col-span-7 md:row-span-2 min-h-[360px]"
            number="01"
            icon={<MoonStars weight="thin" size={28} />}
            title="Write, unedited."
            body="A blank page, with moods and tags there if you want them. No metrics to chase. Just you, writing what's actually there."
            image={JOURNAL_IMG}
          />
          <BentoCard
            className="md:col-span-5 min-h-[200px]"
            number="02"
            icon={<Sparkle weight="thin" size={24} />}
            title="The mirror replies."
            body="Claude reads with care and names what it notices: the emotion under the words, the question hiding in plain sight."
          />
          <BentoCard
            className="md:col-span-5 min-h-[200px]"
            number="03"
            icon={<Compass weight="thin" size={24} />}
            title="Patterns surface."
            body="Themes, triggers and recurring choices, quietly assembled across entries so you can finally see them."
          />
          <BentoCard
            className="md:col-span-6 min-h-[220px]"
            number="04"
            icon={<Lightning weight="thin" size={24} />}
            title="Weekly clarity."
            body="A short, honest summary each week. What moved, what stuck, and one small invitation forward."
            image={LIGHT_IMG}
          />
          <BentoCard
            className="md:col-span-6 min-h-[220px]"
            title="Guided prompts, when stuck."
            body="Ten thoughtful prompts ready in the sidebar. Pick one when the blank page feels too blank."
            accent
          />
        </div>
      </section>

      {/* Features strip */}
      <section id="features" className="px-5 sm:px-8 md:px-16 py-16 sm:py-24 border-t border-line">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div className="rounded-[2rem] overflow-hidden bg-canvas-warm aspect-[5/4]">
            <img src={INSIGHTS_IMG} alt="Flowing lines representing clarity" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute mb-6">Why it works</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-8">
              Not advice. <em className="italic text-clay">Reflection.</em>
            </h2>
            <ul className="space-y-5 text-ink-soft text-lg leading-relaxed">
              <li className="flex gap-4">
                <span className="font-serif-display text-2xl text-clay leading-none">·</span>
                <span>You’re not told what to feel. You’re shown what you already are.</span>
              </li>
              <li className="flex gap-4">
                <span className="font-serif-display text-2xl text-clay leading-none">·</span>
                <span>Three gentle questions after every entry. The kind a thoughtful friend would ask.</span>
              </li>
              <li className="flex gap-4">
                <span className="font-serif-display text-2xl text-clay leading-none">·</span>
                <span>Your words stay yours. Private journal, encrypted at rest.</span>
              </li>
            </ul>
            <Link
              to={user ? "/app" : "/auth"}
              data-testid="features-cta"
              className="inline-flex items-center gap-2 mt-10 rounded-full bg-ink text-canvas px-7 py-3.5 text-sm font-medium hover:bg-ink/90 transition-colors active:scale-95"
            >
              Begin your first entry
              <ArrowRight weight="bold" size={16} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-5 sm:px-8 md:px-16 py-8 sm:py-10 border-t border-line flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-ink-mute">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-clay/90" />
          <span className="font-serif-display text-lg text-ink">Clarity Mirror</span>
        </div>
        <p>© {year} · A quieter way to know yourself.</p>
        <Link to="/privacy-promise" data-testid="footer-privacy-promise-link" className="underline underline-offset-4 hover:text-ink transition-colors">
          How we protect you
        </Link>
        <Link to="/terms" data-testid="footer-terms-link" className="underline underline-offset-4 hover:text-ink transition-colors">
          User agreement &amp; privacy
        </Link>
        <Link to="/terms-of-service" data-testid="footer-tos-link" className="underline underline-offset-4 hover:text-ink transition-colors">
          Terms of Service
        </Link>
      </footer>
    </div>
  );
}

function BentoCard({ className = "", number, icon, title, body, image, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
      className={`relative rounded-[1.75rem] sm:rounded-[2rem] p-6 sm:p-8 md:p-10 border border-line bg-canvas-warm overflow-hidden group hover:border-line-strong transition-colors ${
        accent ? "bg-clay/10" : ""
      } ${className}`}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3 text-ink-soft">
          {icon}
          {number && <span className="font-mono text-xs tracking-widest text-ink-mute">{number}</span>}
        </div>
      </div>
      <h3 className="font-serif-display text-2xl sm:text-3xl md:text-4xl leading-tight mb-3 sm:mb-4 text-ink">{title}</h3>
      <p className="text-ink-soft leading-relaxed text-sm sm:text-base md:text-lg max-w-md">{body}</p>
      {image && (
        <div className="mt-8 rounded-2xl overflow-hidden aspect-[16/9]">
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </motion.div>
  );
}
